import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Phone, MessageCircle, Mail, CheckCircle2, Clock, Shield, FileText, MapPin } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { Button } from '../components/ui/button';
import { businessInfo } from '../mockData';

export const EvbNummerPage = () => {
  const openWhatsAppEvb = () => {
    const message = encodeURIComponent('Hallo, ich benötige eine eVB-Nummer für meine Kfz-Zulassung.');
    window.open(`https://wa.me/${businessInfo.whatsappNumber}?text=${message}`, '_blank');
  };

  const scrollToContact = () => {
    const element = document.getElementById('evb-contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <Helmet>
        <title>eVB-Nummer sofort erhalten - Kreis Mettmann & Velbert | HDI Hauptvertretung</title>
        <meta name="description" content="eVB-Nummer für Ihre Kfz-Zulassung im Kreis Mettmann. HDI Hauptvertretung Mazlum Aksu - Schnelle Beratung & Ausstellung für Velbert, Mettmann, Heiligenhaus, Haan und weitere Städte." />
        <meta name="keywords" content="eVB Nummer Kreis Mettmann, eVB Nummer Velbert, eVB Nummer HDI, eVB Nummer sofort, eVB Nummer bekommen, eVB Nummer erhalten, eVB Nummer Auto" />
        <link rel="canonical" href="https://mazulassungsdienst.de/evb-nummer" />
        <meta property="og:title" content="eVB-Nummer sofort - HDI Hauptvertretung Kreis Mettmann" />
        <meta property="og:description" content="Schnelle eVB-Nummer für Ihre Kfz-Zulassung. Persönliche Beratung durch HDI Hauptvertretung Mazlum Aksu." />
        <meta property="og:url" content="https://mazulassungsdienst.de/evb-nummer" />
      </Helmet>

      <div className="min-h-screen bg-white">
        <Header />
        
        <main>
          {/* Hero Section */}
          <section className="relative pt-32 pb-20 bg-gradient-to-b from-blue-50 to-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center max-w-4xl mx-auto">
                <div className="inline-flex items-center space-x-2 bg-green-100 text-green-800 px-4 py-2 rounded-full mb-6">
                  <Shield className="w-4 h-4" />
                  <span className="text-sm font-semibold">HDI Hauptvertretung Mazlum Aksu</span>
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                  eVB-Nummer sofort erhalten<br />
                  <span className="text-blue-700">Kreis Mettmann & Velbert</span>
                </h1>
                
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  Sie benötigen eine eVB-Nummer für Ihre Kfz-Zulassung? Als HDI Hauptvertretung bieten wir Ihnen persönliche Beratung und schnelle Ausstellung nach Prüfung Ihrer Anfrage.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg" 
                    onClick={openWhatsAppEvb}
                    className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    eVB per WhatsApp anfordern
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={() => window.location.href = `tel:${businessInfo.phone}`}
                    className="border-blue-700 text-blue-700 hover:bg-blue-50 px-8 py-6 text-lg"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Jetzt anrufen
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Was ist eine eVB-Nummer? */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                  Was ist eine eVB-Nummer?
                </h2>
                <div className="prose prose-lg text-gray-600">
                  <p className="mb-4">
                    Die <strong>elektronische Versicherungsbestätigung (eVB-Nummer)</strong> ist eine siebenstellige Nummer, die Sie für die Zulassung Ihres Fahrzeugs bei der Zulassungsstelle benötigen. Sie bestätigt, dass für Ihr Fahrzeug eine gültige Kfz-Haftpflichtversicherung abgeschlossen wurde.
                  </p>
                  <p className="mb-4">
                    Ohne eVB-Nummer ist keine Fahrzeugzulassung möglich. Die eVB-Nummer ersetzt die frühere Doppelkarte und macht den Zulassungsprozess deutlich schneller und einfacher.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Vorteile HDI */}
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                  Warum eVB-Nummer bei HDI Hauptvertretung Mazlum Aksu?
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Als offizielle HDI Hauptvertretung in Velbert-Langenberg bieten wir Ihnen persönlichen Service und faire Konditionen.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Clock className="w-6 h-6 text-blue-700" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Schnelle Bearbeitung
                  </h3>
                  <p className="text-gray-600">
                    Nach Prüfung Ihrer Angaben erhalten Sie Ihre eVB-Nummer zeitnah per E-Mail oder SMS.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-6 h-6 text-green-700" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Persönliche Beratung
                  </h3>
                  <p className="text-gray-600">
                    Individuelle Beratung zu Tarifen, Deckungssummen und möglichen Rabatten.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                    <Shield className="w-6 h-6 text-yellow-700" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    HDI-Qualität
                  </h3>
                  <p className="text-gray-600">
                    Profitieren Sie von einem der führenden Versicherer in Deutschland.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                    <FileText className="w-6 h-6 text-purple-700" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Alles aus einer Hand
                  </h3>
                  <p className="text-gray-600">
                    Kombination aus Versicherung und Zulassungsservice bei M.A. Zulassungsdienst.
                  </p>
                </div>
              </div>

              <div className="mt-12 bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center">
                <p className="text-lg font-semibold text-green-900 mb-2">
                  🎁 Neukundenrabatt bis zu 20%
                </p>
                <p className="text-gray-700">
                  Profitieren Sie von attraktiven Konditionen für Neukunden bei HDI.
                </p>
              </div>
            </div>
          </section>

          {/* Lokale Abdeckung */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                  eVB-Nummer für den gesamten Kreis Mettmann
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Wir sind Ihr Ansprechpartner für eVB-Nummern und Kfz-Versicherungen in allen Städten des Kreises Mettmann.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {[
                  { city: 'Velbert', highlight: true },
                  { city: 'Mettmann', highlight: false },
                  { city: 'Heiligenhaus', highlight: false },
                  { city: 'Haan', highlight: false },
                  { city: 'Erkrath', highlight: false },
                  { city: 'Ratingen', highlight: false },
                  { city: 'Hilden', highlight: false },
                  { city: 'Langenfeld', highlight: false },
                  { city: 'Monheim am Rhein', highlight: false },
                ].map((location, index) => (
                  <div 
                    key={index} 
                    className={`flex items-center space-x-3 p-4 rounded-lg ${location.highlight ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50'}`}
                  >
                    <MapPin className={`w-5 h-5 ${location.highlight ? 'text-blue-700' : 'text-gray-600'}`} />
                    <span className={`text-lg font-semibold ${location.highlight ? 'text-blue-900' : 'text-gray-900'}`}>
                      {location.city}
                      {location.highlight && <span className="ml-2 text-sm text-blue-600">(Hauptsitz)</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* So erhalten Sie Ihre eVB-Nummer */}
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                  So erhalten Sie Ihre eVB-Nummer
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  In vier einfachen Schritten zu Ihrer elektronischen Versicherungsbestätigung
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {[
                  {
                    step: '1',
                    title: 'Kontakt aufnehmen',
                    description: 'Rufen Sie uns an, schreiben Sie per WhatsApp oder nutzen Sie unser Kontaktformular.'
                  },
                  {
                    step: '2',
                    title: 'Beratungsgespräch',
                    description: 'Wir besprechen Ihre Anforderungen und prüfen die besten Tarife für Sie.'
                  },
                  {
                    step: '3',
                    title: 'Versicherung abschließen',
                    description: 'Nach Ihrer Zustimmung schließen wir die Kfz-Versicherung für Sie ab.'
                  },
                  {
                    step: '4',
                    title: 'eVB-Nummer erhalten',
                    description: 'Sie erhalten Ihre eVB-Nummer per E-Mail oder SMS und können direkt zur Zulassungsstelle.'
                  }
                ].map((item, index) => (
                  <div key={index} className="relative">
                    <div className="bg-white p-6 rounded-xl shadow-sm h-full">
                      <div className="w-12 h-12 bg-blue-700 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">
                        {item.step}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-gray-600">
                        {item.description}
                      </p>
                    </div>
                    {index < 3 && (
                      <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-blue-200"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-20 bg-white">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
                Häufige Fragen zur eVB-Nummer
              </h2>

              <div className="space-y-6">
                {[
                  {
                    question: 'Was kostet eine eVB-Nummer?',
                    answer: 'Die eVB-Nummer selbst ist kostenlos. Sie ist Bestandteil Ihrer Kfz-Haftpflichtversicherung. Die Kosten richten sich nach dem gewählten Versicherungstarif, Ihrer Schadenfreiheitsklasse und weiteren Faktoren.'
                  },
                  {
                    question: 'Wie lange ist die eVB-Nummer gültig?',
                    answer: 'Die eVB-Nummer ist in der Regel bis zum Beginn des Versicherungsschutzes bzw. für einen Zeitraum von mehreren Monaten gültig. Nach Zulassung Ihres Fahrzeugs wird sie mit der Versicherung verknüpft.'
                  },
                  {
                    question: 'Kann ich die eVB-Nummer sofort bekommen?',
                    answer: 'Nach Abschluss der Kfz-Versicherung und Prüfung Ihrer Angaben erhalten Sie die eVB-Nummer in der Regel innerhalb kurzer Zeit. Wir bemühen uns um schnellstmögliche Bearbeitung, können aber keine sofortige Ausstellung ohne Prüfung garantieren.'
                  },
                  {
                    question: 'Was passiert, wenn ich die Versicherung wechsle?',
                    answer: 'Bei einem Versicherungswechsel erhalten Sie von Ihrem neuen Versicherer eine neue eVB-Nummer. Die alte eVB-Nummer verliert ihre Gültigkeit, sobald der neue Vertrag aktiv ist.'
                  },
                  {
                    question: 'Brauche ich eine eVB-Nummer für jedes Fahrzeug?',
                    answer: 'Ja, für jedes zuzulassende Fahrzeug benötigen Sie eine separate eVB-Nummer. Jede Nummer ist fahrzeugspezifisch und kann nur einmal für die Zulassung verwendet werden.'
                  },
                  {
                    question: 'Was muss ich für die eVB-Nummer bereithalten?',
                    answer: 'Für die Versicherungsberatung benötigen wir: Fahrzeugschein oder Fahrzeugbrief, Ihre persönlichen Daten, Angaben zu Schadenfreiheitsklasse (falls vorhanden) und ggf. Informationen zu Vorversicherungen.'
                  }
                ].map((faq, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {faq.question}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact CTA */}
          <section id="evb-contact" className="py-20 bg-blue-700 text-white">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Jetzt eVB-Nummer anfordern
              </h2>
              <p className="text-xl mb-8 text-blue-100">
                Kontaktieren Sie uns für eine persönliche Beratung und Ihre eVB-Nummer
              </p>

              <div className="grid sm:grid-cols-3 gap-6 mb-8">
                <a 
                  href={`https://wa.me/${businessInfo.whatsappNumber}?text=${encodeURIComponent('Hallo, ich benötige eine eVB-Nummer für meine Kfz-Zulassung.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-gray-900 p-6 rounded-xl hover:shadow-lg transition-shadow"
                >
                  <MessageCircle className="w-8 h-8 mx-auto mb-3 text-green-600" />
                  <div className="font-bold mb-1">WhatsApp</div>
                  <div className="text-sm text-gray-600">Schnellste Antwort</div>
                </a>

                <a 
                  href={`tel:${businessInfo.phone}`}
                  className="bg-white text-gray-900 p-6 rounded-xl hover:shadow-lg transition-shadow"
                >
                  <Phone className="w-8 h-8 mx-auto mb-3 text-blue-600" />
                  <div className="font-bold mb-1">{businessInfo.phoneDisplay}</div>
                  <div className="text-sm text-gray-600">Mo-Do 10-17:30 Uhr</div>
                </a>

                <button 
                  onClick={scrollToContact}
                  className="bg-white text-gray-900 p-6 rounded-xl hover:shadow-lg transition-shadow"
                >
                  <Mail className="w-8 h-8 mx-auto mb-3 text-purple-600" />
                  <div className="font-bold mb-1">Kontaktformular</div>
                  <div className="text-sm text-gray-600">24/7 erreichbar</div>
                </button>
              </div>

              <div className="bg-blue-800 rounded-xl p-6 text-left">
                <p className="text-sm text-blue-200">
                  <strong className="text-white">Hinweis:</strong> Die eVB-Nummer wird nach Prüfung Ihrer Angaben und Abschluss der Kfz-Versicherung ausgestellt. Der Service wird angeboten durch die HDI Hauptvertretung Mazlum Aksu, Velbert-Langenberg. M.A. Zulassungsdienst ist ein eigenständiger Zulassungsservice und keine Versicherungsagentur.
                </p>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <WhatsAppButton />
      </div>
    </>
  );
};
